import { useState } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

interface DiscardDraftDialogProps {
  onConfirm: () => Promise<void>;
  disabled?: boolean;
}

export function DiscardDraftDialog({ onConfirm, disabled }: DiscardDraftDialogProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      setOpen(false);
    } catch {
      // Error handling is done by the caller
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
      >
        <Trash2 className="w-4 h-4" />
        Discard Draft
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Discard Draft?</DialogTitle>
            <DialogDescription>
              Are you sure you want to discard all draft changes?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded-lg bg-destructive/5 border border-destructive/20 p-4 space-y-2 text-sm text-muted-foreground">
              <p>This action will:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Permanently discard all changes in your draft</li>
                <li>Your active profile will remain unchanged</li>
                <li>You can create a new draft afterward</li>
              </ul>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>
              Keep Draft
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={loading}
              className="gap-2"
            >
              <Trash2 className="w-4 h-4" />
              {loading ? "Discarding..." : "Discard Draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
